"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import type { SpinningWheelUIReward } from "@cede/types";
import { WheelCanvas } from "./WheelCanvas/WheelCanvas";
import { WheelTicketsDisplay, WheelSpinButtons, WheelStatusMessage } from "./WheelControls";
import { WheelRewardsDialog } from "./WheelRewards";
import { WheelArrowIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { WHEEL_SPIN_PHASES } from "@/constants/spinningWheel";
import { useHubUser } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useSpinningWheelSpin } from "@/hooks/spinningWheel/useSpinningWheelSpin";
import { useSpinningWheelTickets } from "@/hooks/spinningWheel/useSpinningWheelTickets";
import { useWheelAnimation } from "@/hooks/spinningWheel/useWheelAnimation";
import { useWheelRateLimit } from "@/hooks/spinningWheel/useWheelRateLimit";
import { useWheelResultCollection } from "@/hooks/spinningWheel/useWheelResultCollection";
import { useWheelRewards } from "@/hooks/spinningWheel/useWheelRewards";
import { calculateRewardAnalytics, sortRewardsBySize } from "@/utils/wheel";
import { useRouter } from "next/navigation";
import { useSnackbar } from "notistack";

interface WheelContainerProps {
  onSpinComplete?: (rewards: SpinningWheelUIReward[]) => void;
  onClose?: () => void;
  diameter?: number;
  onViewHistory?: () => void;
  isOpen?: boolean;
}

/**
 * Spinning wheel container with proper phase-based architecture
 * Phase 1: Initial fast spin (3 circles in 1s)
 * Phase 2: Wait for SSE result while slow spinning
 * Phase 3: Landing animation to winning position (6s)
 * Phase 4: Show results after animation completes
 */
export const WheelContainer: React.FC<WheelContainerProps> = ({
  onSpinComplete,
  onClose,
  diameter = 400,
  onViewHistory,
  isOpen = true,
}) => {
  const [spinResults, setSpinResults] = useState<SpinningWheelUIReward[]>([]);
  const [isRewardsDialogOpen, setIsRewardsDialogOpen] = useState(false);
  const isMultiSpinRef = useRef(false);
  const analyticsTrackedRef = useRef(false);

  // Feature flag to enable/disable spinning wheel dialog
  const isSpinningWheelEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.SPINNING_WHEEL_HUB_ENABLE);

  const { enqueueSnackbar } = useSnackbar();
  const { authenticated, login, hubUser } = useHubUser();
  const router = useRouter();
  const { trackEvent } = useMetricsEvents();

  // Fetch rewards and tickets (only when modal is open)
  const { rewards, isLoading: rewardsLoading } = useWheelRewards({ enabled: isOpen });
  const { tickets, isLoading: ticketsLoading, refetch: refetchTickets } = useSpinningWheelTickets({ enabled: isOpen });

  // Custom hooks for animation, rate limiting, and result collection
  const animation = useWheelAnimation({
    rewards,
    onAnimationComplete: (results) => showResults(results),
  });

  const rateLimit = useWheelRateLimit();

  const resultCollection = useWheelResultCollection({
    rewards,
    hubUserId: hubUser?.id?.toString(),
    spinPhase: animation.spinPhase,
    onResultsCollected: (biggestWin, biggestWinIndex, allResults) => {
      // Trigger landing animation with all collected results
      if (animation.spinPhase === WHEEL_SPIN_PHASES.INITIAL_SPIN) {
        animation.setPendingResult(biggestWin, biggestWinIndex, allResults);
      } else {
        animation.startLandingAnimation(biggestWin, biggestWinIndex, allResults);
      }

      // Track analytics only once per spin
      if (!analyticsTrackedRef.current) {
        analyticsTrackedRef.current = true;

        const analytics = calculateRewardAnalytics(allResults);

        // Track when all spinning wheel results are collected via SSE
        trackEvent(METRICS_EVENTS.SPINNING_WHEEL_RESULTS_COLLECTED, {
          spinCount: allResults.length,
          totalUSDT: analytics.totalUSDT,
          totalETH: analytics.totalETH,
          usdtWinCount: analytics.usdtWinCount,
          ethWinCount: analytics.ethWinCount,
          spinResults: allResults.map((result) => `${result.rewardAmount.toNumber()} ${result.rewardSymbol}`),
        });
      }
    },
    onTimeout: handleSpinTimeout,
    onMultiSpinComplete: (results) => {
      // For multi-spin, show dialog immediately with all results
      const sortedResults = sortRewardsBySize(results);

      // Mark as multi-spin to prevent onAnimationComplete from overriding
      isMultiSpinRef.current = true;

      setSpinResults(sortedResults);
      setIsRewardsDialogOpen(true);
      refetchTickets();
      onSpinComplete?.(sortedResults);
    },
  });

  // Backend spin function with error handling
  const { spin: backendSpin } = useSpinningWheelSpin({
    onError: (error) => {
      const errorMessage = error.message || "Failed to spin the wheel";
      // Track when spinning wheel spin fails with error
      trackEvent(METRICS_EVENTS.SPINNING_WHEEL_SPIN_FAILED, {
        error: errorMessage,
      });
      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 5000,
      });
      animation.resetAnimation();
      resultCollection.stopCollecting();
    },
  });

  /**
   * Handle timeout
   */
  function handleSpinTimeout() {
    // Track when spinning wheel spin times out waiting for SSE results
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_SPIN_FAILED, {
      error: "Timeout - no result received",
      availableTickets: tickets,
      failureType: "timeout",
      expectedSpinCount: resultCollection.expectedSpinCount,
      collectedResultsCount: resultCollection.collectedResults.length,
    });
    animation.resetAnimation();
    resultCollection.stopCollecting();
    enqueueSnackbar("Spin timeout - no result received. Please try again.", {
      variant: "error",
      autoHideDuration: 8000,
    });
  }

  /**
   * Show results dialog
   */
  const showResults = useCallback(
    (results: SpinningWheelUIReward[]) => {
      // Skip if this is a multi-spin (already shown via onMultiSpinComplete)
      // Don't reset the flag here - it will be reset when starting the next spin
      if (isMultiSpinRef.current) {
        return;
      }

      const sortedResults = sortRewardsBySize(results);
      setSpinResults(sortedResults);
      setIsRewardsDialogOpen(true);
      refetchTickets();
      onSpinComplete?.(sortedResults);
    },
    [refetchTickets, onSpinComplete],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      animation.cleanupAnimations();
      resultCollection.stopCollecting();
    };
  }, []);

  // Overall loading and spinning state
  const isLoading = rewardsLoading || ticketsLoading;

  // Handle "Get More" button click
  const handleGetMoreTickets = useCallback(() => {
    // Track when user clicks to get more spinning wheel tickets
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_GET_MORE_TICKETS_CLICKED, {
      currentTickets: tickets,
      hubUserId: hubUser?.id,
    });
    onClose?.();
    router.push(ROUTES.QUESTS);
  }, [onClose, router, tickets, hubUser?.id, trackEvent]);

  // Handle spin button click
  const handleSpinClick = useCallback(
    (count: number) => {
      if (!authenticated) {
        login();
        return;
      }

      if (tickets < count) {
        enqueueSnackbar(`Not enough tickets. You have ${tickets}, need ${count}.`, {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }

      if (animation.isSpinning) {
        return;
      }

      // Check rate limit
      const rateLimitCheck = rateLimit.canSpin();
      if (!rateLimitCheck.allowed) {
        if (rateLimitCheck.message) {
          enqueueSnackbar(rateLimitCheck.message, {
            variant: "info",
            autoHideDuration: 3000,
          });
        }
        return;
      }

      // Track when user clicks spin button on spinning wheel
      trackEvent(METRICS_EVENTS.SPINNING_WHEEL_SPIN_BUTTON_CLICKED, {
        spinCount: count,
        availableTickets: tickets,
        hubUserId: hubUser?.id,
      });

      // Clear previous results and multi-spin flag
      setSpinResults([]);
      isMultiSpinRef.current = false;
      analyticsTrackedRef.current = false; // Reset analytics tracking for new spin

      // Register rate limit
      rateLimit.registerSpin();

      // Start animation
      animation.startInitialSpin();

      // Start collecting results
      resultCollection.startCollecting(count);

      // Trigger backend spin
      backendSpin(count);
    },
    [
      authenticated,
      login,
      tickets,
      animation.isSpinning,
      animation.startInitialSpin,
      rateLimit,
      enqueueSnackbar,
      backendSpin,
      hubUser?.id,
      resultCollection.startCollecting,
    ],
  );

  if (!isSpinningWheelEnabled) {
    return (
      <div
        className="relative flex flex-col items-center justify-center max-w-2xl mx-auto gap-8 py-12"
        style={{ minHeight: "70vh" }}
      >
        {/* Coming Soon Badge */}
        <div className="relative">
          {/* Animated glow effect */}
          <div
            className="absolute inset-0 blur-2xl opacity-60 animate-pulse"
            style={{
              background: "radial-gradient(circle, var(--color-cede-primary) 0%, transparent 70%)",
            }}
          />
          {/* Badge */}
          <div
            className="relative px-10 py-5 rounded-2xl border-2 backdrop-blur-xl"
            style={{
              background: "linear-gradient(135deg, rgba(94, 166, 255, 0.2) 0%, rgba(94, 166, 255, 0.1) 100%)",
              borderColor: "var(--color-cede-primary)",
              boxShadow:
                "0 0 30px rgba(94, 166, 255, 0.4), 0 0 60px rgba(94, 166, 255, 0.2), inset 0 0 20px rgba(94, 166, 255, 0.15)",
            }}
          >
            <div className="flex flex-col items-center gap-1">
              <span className="text-4xl font-bold text-cede-primary animate-pulse">Coming Soon</span>
              <span className="text-sm text-cede-text-secondary">Spin to Win Big Rewards!</span>
            </div>
          </div>
        </div>

        {/* Information Text and CTA */}
        <div className="w-full flex flex-col items-center gap-4 px-4 max-w-lg mx-auto text-center">
          <p className="text-cede-text-primary text-lg font-semibold">
            The Spinning Wheel will be available soon. Complete quests to earn tickets and get ready to win amazing
            prizes!
          </p>
          <Button
            variant="primary"
            size="large"
            onClick={() => {
              onClose?.();
              router.push(ROUTES.QUESTS);
            }}
          >
            View Quests
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center w-full" style={{ minHeight: `${diameter + 200}px` }}>
      {/* Wheel Container with Arrow */}
      <div
        className="relative flex items-center justify-center mt-8 mb-6"
        style={{ width: `${diameter}px`, height: `${diameter}px` }}
      >
        {/* Top Arrow Pointer */}
        <div
          className="absolute z-30"
          style={{
            top: "-28px",
            left: "50%",
            transform: "translateX(-50%) rotate(-90deg)",
          }}
        >
          <WheelArrowIcon />
        </div>

        <WheelCanvas
          rewards={rewards}
          rotation={animation.currentRotation}
          isSpinning={animation.isLanding}
          winningReward={animation.winningResult}
          isLoading={isLoading}
          diameter={diameter}
        />
      </div>

      {/* Status Message */}
      <WheelStatusMessage isSpinning={animation.isSpinning} clientSideDelay={rateLimit.clientSideDelay} />

      {/* Action Buttons */}
      <div className="w-full flex items-center justify-center gap-4">
        {!authenticated ? (
          <Button onClick={login} className="px-6 py-3 font-semibold">
            Connect Wallet to Spin
          </Button>
        ) : (
          <>
            <WheelTicketsDisplay
              tickets={tickets}
              isLoading={ticketsLoading}
              onGetMore={handleGetMoreTickets}
              onViewHistory={onViewHistory}
            />
            <WheelSpinButtons
              tickets={tickets}
              isSpinning={animation.isSpinning}
              isDisabled={rateLimit.isRateLimited}
              onSpin={handleSpinClick}
            />
          </>
        )}
      </div>

      {/* Rewards Dialog */}
      <WheelRewardsDialog
        isOpen={isRewardsDialogOpen}
        onClose={() => setIsRewardsDialogOpen(false)}
        rewards={spinResults}
      />
    </div>
  );
};
